import { ButtonVariantEnum, ModalTypeEnum } from '../enums';
import { Form, Formik } from 'formik';
import { Modal, ModalFooter } from 'react-bootstrap';
import React, { FunctionComponent, PropsWithChildren, useState } from 'react';

import { Button } from '../components';
import { FaTimes } from 'react-icons/fa';
import { IconHandler } from '../utils';
import { InformationModalVariantEnumType } from '../types/enums/InformationModalVariantEnumType';
import { ModalContext } from '.';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

const ModalProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const [modalMessage, setModalMessage] = useState<string>('');
  const [modalFormContent, setModalFormContent] = useState<Function>(() => {});
  const [modalInformationContent, setModalInformationContent] = useState<any>();
  const [modalInformationVariant, setModalInformationVariant] = useState<InformationModalVariantEnumType>();
  const [modalFormInitialValues, setModalFormInitialValues] = useState({});
  const [modalFormValidationSchema, setModalFormValidationSchema] = useState();
  const [confirmModalAction, setConfirmModalAction] = useState<VoidFunction>(() => {});
  const [confirmButtonVariant, setConfirmButtonVariant] = useState<ButtonVariantEnum>();
  const [cancelationModalCallback, setCancelationModalCallback] = useState<VoidFunction>(() => {});
  const [alertModalBtnTitle, setAlertModalBtnTitle] = useState<string>('');
  const [modalTitle, setModalTitle] = useState<string>('');
  const [modalType, setModalType] = useState<ModalTypeEnum>();
  const [showModal, setShowModal] = useState(false);
  const { t } = useTranslation();

  const alertModal = (message: string, modalBtnTitle: string = '', modalTitle: string = '') => {
    setModalMessage(message);
    setConfirmButtonVariant(ButtonVariantEnum.PRIMARY);
    setModalType(ModalTypeEnum.ALERT);
    setAlertModalBtnTitle(modalBtnTitle);
    setModalTitle(modalTitle);
    setShowModal(true);
  };

  const confirmModal = (
    message: string,
    confirmAction: VoidFunction,
    confirmButtonVariant: ButtonVariantEnum = ButtonVariantEnum.PRIMARY,
    modalTitle: string = '',
    cancelationCallback: VoidFunction = () => {},
  ) => {
    setModalMessage(message);
    setConfirmModalAction(() => confirmAction);
    setConfirmButtonVariant(confirmButtonVariant);
    setModalType(ModalTypeEnum.CONFIRM);
    setModalTitle(modalTitle);
    setShowModal(true);
    setCancelationModalCallback(() => cancelationCallback);
  };

  const confirmModalForm = (
    modalContent: any,
    initialValues: any,
    validationSchema: any,
    confirmAction: VoidFunction,
    confirmButtonVariant: ButtonVariantEnum = ButtonVariantEnum.PRIMARY,
    modalTitle: string = '',
    cancelationCallback: VoidFunction = () => {},
  ) => {
    setModalFormContent(() => modalContent);
    setModalFormInitialValues(initialValues);
    setModalFormValidationSchema(validationSchema);
    setConfirmModalAction(() => confirmAction);
    setConfirmButtonVariant(confirmButtonVariant);
    setModalTitle(modalTitle);
    setModalType(ModalTypeEnum.CONFIRM_WITH_FORM);
    setShowModal(true);
    setCancelationModalCallback(() => cancelationCallback);
  };

  const informationModal = (modalContent: any, modalTitle: string = '', modalVariant: InformationModalVariantEnumType) => {
    setModalInformationContent(modalContent);
    setModalInformationVariant(modalVariant);
    setModalTitle(modalTitle);
    setModalType(ModalTypeEnum.INFORMATION);
    setShowModal(true);
  };

  const value = {
    alertModal,
    confirmModal,
    confirmModalForm,
    informationModal,
  };

  return (
    <ModalContext.Provider value={value}>
      <Modal
        show={showModal}
        onHide={() => {
          cancelationModalCallback();
          setShowModal(false);
        }}
        centered
        {...([ModalTypeEnum.CONFIRM_WITH_FORM, ModalTypeEnum.INFORMATION].includes(modalType!) && { size: 'lg' })}
      >
        <Modal.Header
          className={classNames(
            'd-flex flex-row-reverse',
            modalType === ModalTypeEnum.INFORMATION && `alert-${modalInformationVariant?.class}`,
          )}
        >
          <button
            type="button"
            className={`align-self-start btn icon-btn-${
              modalType === ModalTypeEnum.INFORMATION ? modalInformationVariant?.class : 'primary'
            }-700 no-border d-flex circle p-1 text-${
              modalType === ModalTypeEnum.INFORMATION ? modalInformationVariant?.class : 'primary'
            }-700`}
            onClick={() => setShowModal(false)}
          >
            <FaTimes />
          </button>
          {modalType === ModalTypeEnum.INFORMATION ? (
            <Modal.Title className={`text-${modalInformationVariant}-700 text-center w-100 flex-grow-1 fs-3 fw-bold`} as="p">
              <div className="m-0">
                <IconHandler Icon={modalInformationVariant?.icon!} />
                <p>{modalTitle}</p>
              </div>
            </Modal.Title>
          ) : (
            <Modal.Title>{modalTitle}</Modal.Title>
          )}
        </Modal.Header>
        {modalType === ModalTypeEnum.CONFIRM_WITH_FORM ? (
          <Formik
            validateOnMount
            initialValues={modalFormInitialValues}
            validationSchema={modalFormValidationSchema}
            onSubmit={() => {}}
          >
            {(props) => (
              <Form>
                <Modal.Body>{modalFormContent(props)}</Modal.Body>
                <ModalFooter>
                  <Button
                    onClick={() => {
                      cancelationModalCallback();
                      setShowModal(false);
                    }}
                    title={t('label.cancelar')}
                    outline
                  />
                  <Button
                    onClick={() => {
                      confirmModalAction();
                      setShowModal(false);
                    }}
                    title={t('label.confirmar')}
                    variant={confirmButtonVariant}
                    disabled={!props.isValid}
                  />
                </ModalFooter>
              </Form>
            )}
          </Formik>
        ) : modalType === ModalTypeEnum.INFORMATION ? (
          <Modal.Body>{modalInformationContent}</Modal.Body>
        ) : (
          <>
            <Modal.Body>{modalMessage}</Modal.Body>
            <Modal.Footer>
              {modalType === ModalTypeEnum.ALERT ? (
                <Button onClick={() => setShowModal(false)} title={alertModalBtnTitle ? alertModalBtnTitle : t('label.ok')} />
              ) : (
                <>
                  <Button
                    onClick={() => {
                      cancelationModalCallback();
                      setShowModal(false);
                    }}
                    title={t('label.cancelar')}
                    outline
                  />
                  <Button
                    onClick={() => {
                      confirmModalAction();
                      setShowModal(false);
                    }}
                    title={t('label.confirmar')}
                    variant={confirmButtonVariant}
                  />
                </>
              )}
            </Modal.Footer>
          </>
        )}
      </Modal>
      {children}
    </ModalContext.Provider>
  );
};

export default ModalProvider;
